import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { cardSx, colors, typography } from '../constants';
import CostEditor from '../components/CostEditor';
import { Toggle } from '../components/Fields';
import { useScreenLoading } from '../components/ScreenLoading';
import {
  useUnsavedChangesGuard,
  useUnsavedEditor,
} from '../components/UnsavedChanges';
import { ErrorView, RefreshingOverlay } from '../components/States';
import {
  useCostDefaults,
  useCosts,
  useCreateInvoice,
  useDeleteInvoice,
  useImportSales,
  useInvoices,
  useMonthAnalytics,
  usePreviewSales,
  usePutCostDefaults,
  usePutCosts,
  useProfitSettings,
  useUpdateProfitSettings,
} from '../hooks/useProfitData';
import useProfitIqTabs from '../hooks/useProfitIqTabs';
import { useCostTarget, useDataMode, useDateRange } from '../hooks/useSelection';
import { errorMessage, OUTDATED_IMPORT_SERVER } from '../lib/errorMessage';
import { currentMonthStr, fmt, pct } from '../lib/format';

const SECTIONS = [
  { id: 'costs', label: 'Costs', icon: AttachMoneyIcon },
  { id: 'data', label: 'Sales Data', icon: DescriptionOutlinedIcon },
];

const DOC_CATS = [
  { key: 'food', label: 'Food & Beverage', icon: ShoppingBagOutlinedIcon },
  { key: 'labor', label: 'Labor', icon: PeopleOutlineIcon },
  { key: 'rent', label: 'Rent & Lease', icon: HomeOutlinedIcon },
  { key: 'utilities', label: 'Utilities', icon: BoltOutlinedIcon },
  { key: 'packaging', label: 'Packaging', icon: Inventory2OutlinedIcon },
  { key: 'marketing', label: 'Marketing', icon: CampaignOutlinedIcon },
  { key: 'other', label: 'Other', icon: DescriptionOutlinedIcon },
];

function buildMonthOptions() {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    };
  });
}

function monthLabel(key) {
  const [y, m] = key.split('-').map((s) => parseInt(s, 10));
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

const h1Sx = {
  fontSize: typography.heading,
  fontWeight: 800,
  color: colors.foreground,
};

const subSx = {
  fontSize: typography.caption,
  color: colors.mutedForeground,
  mt: 0.5,
  mb: 1.75,
  lineHeight: 1.5,
};

const smallCapsSx = {
  fontSize: typography.caption,
  fontWeight: 700,
  color: colors.mutedForeground,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  mb: 1,
};

const inputSx = {
  width: '100%',
  boxSizing: 'border-box',
  px: 1.5,
  py: 1.25,
  fontSize: typography.body,
  fontFamily: 'inherit',
  color: colors.foreground,
  backgroundColor: colors.card,
  border: `1px solid ${colors.border}`,
  borderRadius: 2.5,
  outline: 'none',
  '&:focus': {
    borderColor: colors.primary,
    boxShadow: `0 0 0 3px ${colors.primary}33`,
  },
  '&::placeholder': { color: colors.faint },
};

function PrimaryButton({ children, disabled, onClick, sx, tone }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: '100%',
        py: 1.5,
        border: 'none',
        borderRadius: 2.5,
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
        fontSize: typography.body,
        fontWeight: 700,
        backgroundColor: disabled ? colors.muted : (tone ?? colors.primary),
        color: disabled ? colors.faint : colors.primaryForeground,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function Note({ tone, children }) {
  const surface =
    tone === 'green'
      ? { bg: colors.successSurface, fg: colors.successSurfaceForeground }
      : { bg: colors.warningSurface, fg: colors.warningSurfaceForeground };
  return (
    <Box
      sx={{
        backgroundColor: surface.bg,
        borderRadius: 2.5,
        p: 1.5,
        mb: 1.75,
      }}
    >
      <Typography
        sx={{
          fontSize: typography.caption,
          color: surface.fg,
          lineHeight: 1.5,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/**
 * Profit IQ's own settings: where sales data comes from and what the monthly
 * costs are.
 *
 * Account, business profile and business deletion are deliberately absent —
 * the portal's own Settings page owns those.
 */
export default function ProfitSettings() {
  const { range, month: filterMonth } = useDateRange();
  const [section, setSection] = useState('costs');
  // Follow the month picked in the date filter (custom/month range); otherwise
  // default to the actual current month.
  const initialMonth =
    (range === 'month' || range === 'custom') && filterMonth
      ? filterMonth
      : currentMonthStr();
  const [costMonth, setCostMonth] = useState(initialMonth);
  useEffect(() => {
    setCostMonth(initialMonth);
  }, [initialMonth]);

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
        {SECTIONS.map((sec) => {
          const active = section === sec.id;
          const Icon = sec.icon;
          return (
            <Box
              component="button"
              type="button"
              key={sec.id}
              onClick={() => setSection(sec.id)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                py: 1.125,
                px: 1.5,
                border: 'none',
                borderRadius: 2.5,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: typography.caption,
                fontWeight: 600,
                backgroundColor: active ? colors.primary : colors.muted,
                color: active
                  ? colors.primaryForeground
                  : colors.mutedForeground,
              }}
            >
              <Icon sx={{ fontSize: 17 }} />
              {sec.label}
            </Box>
          );
        })}
      </Box>

      {section === 'data' ? <SalesDataSection /> : null}
      {section === 'costs' ? (
        <CostsSection costMonth={costMonth} setCostMonth={setCostMonth} />
      ) : null}
    </Box>
  );
}

/* ─────────────────────────── Sales Data ─────────────────────────── */

/**
 * Picks which source every Profit IQ read uses in this browser. The two modes
 * are exclusive: on `online` imported figures are never shown, and on `csv`
 * live orders are never shown, so the active mode is stated plainly.
 */
function DataModeSelector() {
  const { dataMode, setDataMode } = useDataMode();

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={h1Sx}>Data Source</Typography>
      <Typography sx={subSx}>
        {dataMode === 'online'
          ? 'Reading your live orders. Imported CSV files are stored but not counted.'
          : 'Reading only the CSV files you import below. Live orders are not counted.'}
        {' This applies to this browser only.'}
      </Typography>
      <Toggle
        value={dataMode}
        onChange={(next) => {
          if (next === dataMode) return;
          setDataMode(next);
        }}
        options={[
          { value: 'online', label: 'Online orders' },
          { value: 'csv', label: 'CSV import' },
        ]}
      />
    </Box>
  );
}

function SalesDataSection() {
  const previewSales = usePreviewSales();
  const importSales = useImportSales();
  const fileInputRef = useRef(null);
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const [summary, setSummary] = useState(null);

  function onFileChange(event) {
    const file = event.target.files?.[0];
    // Reset so re-picking the same file fires another change event.
    event.target.value = '';
    if (!file) return;
    setState('idle');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setCsv(String(e.target?.result ?? ''));
    reader.onerror = () => {
      setMsg('Could not read that file. Pick the .csv file from your export.');
      setState('error');
    };
    reader.readAsText(file);
  }

  async function handleUpload() {
    if (!csv.trim()) return;
    setState('parsing');
    try {
      // The file is uploaded and parsed once, by the preview; the import then
      // commits that same parse by token.
      const previewed = await previewSales.mutateAsync({ csv });
      if (!previewed.token) {
        setMsg(OUTDATED_IMPORT_SERVER);
        setState('error');
        return;
      }
      const res = await importSales.mutateAsync({
        token: previewed.token,
        fileName: fileName || 'import.csv',
        mode: 'skip',
      });
      setSummary(res.summary);
      setState('success');
    } catch (e) {
      setMsg(errorMessage(e));
      setState('error');
    }
  }

  const canUpload = csv.trim().length > 0;

  return (
    <Box>
      <DataModeSelector />

      <Typography sx={h1Sx}>Sales Data from POS</Typography>
      <Typography sx={subSx}>
        Select a CSV export from Orders.co or Clover POS — the format is
        detected automatically and refreshes all sales, channels, and menu items.
      </Typography>

      <Box
        component="input"
        type="file"
        accept=".csv,text/csv"
        ref={fileInputRef}
        onChange={onFileChange}
        sx={{ display: 'none' }}
      />

      <Box
        component="button"
        type="button"
        onClick={() => fileInputRef.current?.click()}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          py: 4,
          px: 2,
          cursor: 'pointer',
          fontFamily: 'inherit',
          border: `2px dashed ${colors.primary}`,
          backgroundColor: colors.accent,
          borderRadius: 3,
          mb: 1.75,
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 34, color: colors.primary }} />
        <Typography
          sx={{
            fontSize: typography.body,
            fontWeight: 700,
            color: colors.primary,
          }}
        >
          Select a CSV file
        </Typography>
        <Typography sx={{ fontSize: typography.caption, color: colors.faint }}>
          {fileName || 'Orders.co export · .csv format'}
        </Typography>
      </Box>

      <PrimaryButton
        onClick={handleUpload}
        disabled={!canUpload || state === 'parsing'}
      >
        {state === 'parsing' ? 'Parsing…' : 'Parse & Load Data'}
      </PrimaryButton>

      {state === 'success' && summary ? (
        <Box
          sx={{
            mt: 1.75,
            backgroundColor: colors.successSurface,
            borderRadius: 2.5,
            p: 1.75,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 20, color: colors.successSurfaceForeground }}
            />
            <Typography
              sx={{
                fontSize: typography.body,
                fontWeight: 700,
                color: colors.successSurfaceForeground,
              }}
            >
              Data loaded successfully
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <SummaryRow label="Channels" value={String(summary.channels ?? 0)} />
            <SummaryRow
              label="Items imported"
              value={String(summary.itemsImported ?? 0)}
            />
            <SummaryRow
              label="Date range"
              value={`${summary.dateFrom ?? '—'} → ${summary.dateTo ?? '—'}`}
            />
            <SummaryRow
              label="Total days"
              value={String(summary.totalDays ?? 0)}
            />
            <SummaryRow label="Orders" value={String(summary.orderCount ?? 0)} />
            <SummaryRow
              label="Tax rate"
              value={summary.taxRate != null ? pct(summary.taxRate) : '—'}
            />
            <SummaryRow
              label="POS card %"
              value={summary.posCardPct != null ? pct(summary.posCardPct) : '—'}
            />
            <SummaryRow
              label="Total revenue"
              value={fmt(summary.totalRevenue ?? 0)}
            />
          </Box>
        </Box>
      ) : null}

      {state === 'error' ? (
        <Box
          sx={{
            mt: 1.75,
            backgroundColor: colors.destructiveSurface,
            borderRadius: 2.5,
            p: 1.75,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <WarningAmberIcon
              sx={{ fontSize: 20, color: colors.destructiveSurfaceForeground }}
            />
            <Typography
              sx={{
                fontSize: typography.body,
                fontWeight: 700,
                color: colors.destructiveSurfaceForeground,
              }}
            >
              Could not parse file
            </Typography>
          </Box>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: typography.caption,
              color: colors.destructiveSurfaceForeground,
            }}
          >
            {msg}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function SummaryRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Typography
        sx={{
          fontSize: typography.caption,
          color: colors.successSurfaceForeground,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: typography.caption,
          fontWeight: 700,
          color: colors.successSurfaceForeground,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* ─────────────────────────── Costs ─────────────────────────── */

function CostsSection({ costMonth, setCostMonth }) {
  const { goToTab } = useProfitIqTabs();
  const { data: monthDashboard, isFetching: dashboardFetching } =
    useMonthAnalytics(costMonth);
  // `isFetching`, not `isLoading`: a business switch or a save reloads with a
  // cached snapshot still in hand, and the loader has to win over stale lines.
  const {
    data: monthData,
    isFetching: monthFetching,
    error: monthError,
  } = useCosts(costMonth);
  const {
    data: defaultsData,
    isFetching: defaultsFetching,
    error: defaultsError,
  } = useCostDefaults();
  const { data: settings } = useProfitSettings();
  const putCosts = usePutCosts();
  const putDefaults = usePutCostDefaults();
  const updateSettings = useUpdateProfitSettings();

  // Shared with My Costs and the date bar's picker, so all three agree on
  // whether the business is editing a month or the defaults template.
  const { editingDefaults, setCostTarget } = useCostTarget();
  const [snap, setSnap] = useState(null);
  const [dirty, setDirty] = useState(false);
  // Read by the seeding effect, which must not re-run just because the flag
  // flipped — only when fresh server data actually arrives.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const [savedFlash, setSavedFlash] = useState(false);

  // Nothing may leave these edits behind without asking: the section shell
  // guards the tabs and every route out, the header guards the date bar, and
  // the month picker on this page goes through `guardMove` itself.
  const guardMove = useUnsavedChangesGuard();
  useUnsavedEditor({
    isDirty: () => dirtyRef.current,
    save: handleSave,
    discard: discardEdits,
  });

  const data = editingDefaults ? defaultsData : monthData;
  // Every percentage in the editor is measured against the month's revenue and
  // item sales, so the section is still loading until those land too —
  // otherwise a month change paints the lines against a revenue of zero.
  const isFetching =
    (editingDefaults ? defaultsFetching : monthFetching) || dashboardFetching;
  // Keep the editor mounted through a save's refetch; only a month that is
  // still missing something blocks. Both reads count: the snapshot holds the
  // lines, and the month's revenue and item sales are what every percentage is
  // measured against — with the snapshot cached from My Costs and the dashboard
  // still in flight, gating on the snapshot alone reported nothing and the page
  // loaded with no spinner.
  const showLoader = isFetching && (!snap || !monthDashboard);
  useScreenLoading('settings', showLoader ? 'Loading month…' : null);
  const error = editingDefaults ? defaultsError : monthError;

  useEffect(() => {
    if (!data) {
      // No cache entry for this business/month yet, so there is nothing to
      // edit: drop the local copy instead of leaving the previous selection's
      // lines under the loader.
      setSnap(null);
      setDirty(false);
      return;
    }
    // Unsaved edits outrank a background refetch: the Save button is still
    // offering them, so adopting the server copy here would discard work the
    // business can see on screen.
    if (dirtyRef.current) return;
    setSnap({
      costCats: JSON.parse(JSON.stringify(data.costCats)),
      foodCostOverride: data.foodCostOverride,
      salesTaxPct: data.salesTaxPct,
    });
    setDirty(false);
  }, [data]);

  // Fast lookup of default cost lines for the per-line "reset to default"
  // affordance shown while editing a specific month.
  const defaultLines = useMemo(() => {
    const map = new Map();
    (defaultsData?.costCats ?? []).forEach((c) => {
      c.lines.forEach((l) => map.set(`${c.key}:${l.lineId}`, l));
    });
    return map;
  }, [defaultsData]);

  function lineDiffersFromDefault(catKey, lineId) {
    const d = defaultLines.get(`${catKey}:${lineId}`);
    if (!d) return false;
    const cur = snap?.costCats
      .find((c) => c.key === catKey)
      ?.lines.find((l) => l.lineId === lineId);
    if (!cur) return false;
    return (
      (cur.total ?? null) !== (d.total ?? null) ||
      (cur.wage ?? null) !== (d.wage ?? null) ||
      (cur.hours ?? null) !== (d.hours ?? null) ||
      cur.name !== d.name ||
      cur.type !== d.type
    );
  }

  function resetLineToDefault(catKey, lineId) {
    const d = defaultLines.get(`${catKey}:${lineId}`);
    if (!d) return;
    mutateSnap((prev) => ({
      ...prev,
      costCats: prev.costCats.map((c) =>
        c.key !== catKey
          ? c
          : {
              ...c,
              lines: c.lines.map((l) => (l.lineId === lineId ? { ...d } : l)),
            }
      ),
    }));
  }

  const isCurrentMonth = costMonth === currentMonthStr();

  // Cost lines are stored per 30-day month, so the figures for the month being
  // edited are scaled to 30 days before they are compared against them. For the
  // current month that window is only the days elapsed so far.
  const dataDays = monthDashboard?.business.dataDays || 30;
  const scale30 = 30 / dataDays;
  const monthlyRev = (monthDashboard?.totalRevenue ?? 0) * scale30;
  // All three come off the dashboard now. They were summed here, from every item
  // of the month, which is why this page had to read the whole menu to show a
  // percentage — and why the figure could drift from the identical one in My
  // Costs whenever the two additions disagreed about how an item is costed.
  const itemRevRaw = monthDashboard?.totalItemRevenue ?? 0;
  const derivedFoodPct = monthDashboard?.derivedFoodCostPct ?? 0;
  const monthlyItemRev = itemRevRaw * scale30;

  function selectMonth(m) {
    // Switching month reseeds the editor from that month's snapshot, so the
    // edits on screen would be gone without asking first.
    guardMove(() => {
      setCostMonth(m);
      // Compared against the settings payload rather than the dashboard: the
      // dashboard is the date-filtered analytics query, so gating on it dropped
      // the write whenever a month was picked before analytics had resolved.
      if (settings && m !== settings.currentMonth) {
        updateSettings.mutate({ currentMonth: m });
      }
    });
  }

  function mutateSnap(fn) {
    setSnap((prev) => (prev ? fn(prev) : prev));
    setDirty(true);
  }

  /** Reports whether the figures reached the server, for the leave prompt. */
  async function handleSave() {
    if (!snap || !dirty) return true;
    const body = {
      costCats: snap.costCats,
      foodCostOverride: snap.foodCostOverride,
      salesTaxPct: snap.salesTaxPct,
    };
    try {
      if (editingDefaults) {
        await putDefaults.mutateAsync(body);
      } else {
        await putCosts.mutateAsync({ month: costMonth, body });
      }
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
      return true;
    } catch {
      // The draft stays dirty and Save stays enabled so the owner can retry.
      return false;
    }
  }

  /** Back to the saved figures, so the edits stop being offered anywhere. */
  function discardEdits() {
    setDirty(false);
    setSnap(
      data
        ? {
            costCats: JSON.parse(JSON.stringify(data.costCats)),
            foodCostOverride: data.foodCostOverride,
            salesTaxPct: data.salesTaxPct,
          }
        : null
    );
  }

  const editorCats = (snap?.costCats ?? []).map((cat) => {
    if (cat.key === 'food') {
      const itemsPct = derivedFoodPct;
      const effPct =
        snap?.foodCostOverride != null ? snap.foodCostOverride : itemsPct;
      const wastePct = Math.round((effPct - itemsPct) * 10) / 10;
      return {
        ...cat,
        totalPct: effPct,
        isDerived: true,
        isOverridden: snap?.foodCostOverride != null,
        itemsPct,
        wastePct,
        itemsCost: monthlyItemRev * (itemsPct / 100),
        wasteCost: monthlyItemRev * (wastePct / 100),
        scaledLinesTotal: monthlyItemRev * (effPct / 100),
      };
    }
    if (cat.lines.length === 0) {
      return { ...cat, scaledLinesTotal: monthlyRev * (cat.totalPct / 100) };
    }
    const monthlyTotal = cat.lines.reduce(
      (acc, l) =>
        acc +
        (l.type === 'hourly' ? (l.wage || 0) * (l.hours || 0) : l.total || 0),
      0
    );
    const livePct =
      monthlyRev > 0
        ? Math.round((monthlyTotal / monthlyRev) * 1000) / 10
        : cat.totalPct;
    return { ...cat, totalPct: livePct, scaledLinesTotal: monthlyTotal };
  });

  const salesTaxPct = snap?.salesTaxPct ?? 0;
  const salesTaxCollected = monthlyRev * (salesTaxPct / 100);

  return (
    <Box sx={{ position: 'relative' }}>
      <RefreshingOverlay visible={isFetching && !showLoader} />

      <Typography sx={h1Sx}>Monthly Costs</Typography>
      <Typography sx={subSx}>
        Edit the cost lines exactly like My Costs — open each to add line items.
        Pick a month, edit, and Save. Expenses change month to month.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <ModeButton
          active={!editingDefaults}
          icon={CalendarMonthOutlinedIcon}
          label="Monthly costs"
          onClick={() => setCostTarget('month')}
        />
        <ModeButton
          active={editingDefaults}
          icon={TuneIcon}
          label="Monthly defaults"
          onClick={() => setCostTarget('defaults')}
        />
      </Box>

      {editingDefaults ? (
        <Note tone="amber">
          These are your default costs. Every new month starts with these values
          — months you&apos;ve already saved are not changed.
        </Note>
      ) : (
        <>
          <Typography sx={smallCapsSx}>Editing month</Typography>
          <MonthPicker value={costMonth} onSelect={selectMonth} markCurrent />
          <Note tone={isCurrentMonth ? 'green' : 'amber'}>
            {isCurrentMonth
              ? 'This is the current month — these are the same costs shown in My Costs. Editing here updates there instantly, and vice versa.'
              : `You're editing ${monthLabel(costMonth)}. Save to store this month's actuals.`}
          </Note>
        </>
      )}

      {error && !snap ? (
        <ErrorView message="Could not load this month." />
      ) : null}

      {snap ? (
        <>
          <CostEditor
            cats={editorCats}
            totalRevenue={monthlyRev}
            rangeLabel="This month"
            salesTax={{
              pct: salesTaxPct,
              collected: salesTaxCollected,
              customerPaid: monthlyRev + salesTaxCollected,
              revenue: monthlyRev,
            }}
            onCatPct={(key, raw) => {
              const v = parseFloat(raw);
              if (isNaN(v) || v < 0 || v > 100) return;
              mutateSnap((prev) => ({
                ...prev,
                costCats: prev.costCats.map((c) =>
                  c.key === key ? { ...c, totalPct: v } : c
                ),
              }));
            }}
            onFoodOverride={(raw) => {
              const v = parseFloat(raw);
              if (isNaN(v) || v < 0 || v > 95) return;
              mutateSnap((prev) => ({ ...prev, foodCostOverride: v }));
            }}
            onResetFood={() =>
              mutateSnap((prev) => ({ ...prev, foodCostOverride: null }))
            }
            onLine={(catKey, lineId, field, raw) => {
              const v = parseFloat(raw);
              if (isNaN(v)) return;
              mutateSnap((prev) => ({
                ...prev,
                costCats: prev.costCats.map((c) => {
                  if (c.key !== catKey) return c;
                  return {
                    ...c,
                    lines: c.lines.map((l) => {
                      if (l.lineId !== lineId) return l;
                      const u = { ...l, [field]: v };
                      if (u.type === 'hourly' && u.wage && u.hours) {
                        u.total = u.wage * u.hours;
                      }
                      return u;
                    }),
                  };
                }),
              }));
            }}
            onLineName={(catKey, lineId, name) =>
              mutateSnap((prev) => ({
                ...prev,
                costCats: prev.costCats.map((c) =>
                  c.key !== catKey
                    ? c
                    : {
                        ...c,
                        lines: c.lines.map((l) =>
                          l.lineId === lineId ? { ...l, name } : l
                        ),
                      }
                ),
              }))
            }
            onAddLine={(catKey, type) => {
              const nl =
                type === 'hourly'
                  ? {
                      lineId: Date.now(),
                      name: 'New employee',
                      type: 'hourly',
                      wage: 15,
                      hours: 160,
                      total: 2400,
                    }
                  : {
                      lineId: Date.now(),
                      name: 'New expense',
                      type: 'fixed',
                      total: 0,
                      wage: null,
                      hours: null,
                    };
              mutateSnap((prev) => ({
                ...prev,
                costCats: prev.costCats.map((c) =>
                  c.key !== catKey ? c : { ...c, lines: [...c.lines, nl] }
                ),
              }));
            }}
            onDeleteLine={(catKey, lineId) =>
              mutateSnap((prev) => ({
                ...prev,
                costCats: prev.costCats.map((c) =>
                  c.key !== catKey
                    ? c
                    : { ...c, lines: c.lines.filter((l) => l.lineId !== lineId) }
                ),
              }))
            }
            onSalesTax={(raw) => {
              const v = parseFloat(raw);
              if (isNaN(v) || v < 0 || v > 30) return;
              mutateSnap((prev) => ({ ...prev, salesTaxPct: v }));
            }}
            onEditItemCosts={() => goToTab('menu')}
            isLineResettable={
              editingDefaults ? undefined : lineDiffersFromDefault
            }
            onResetLine={editingDefaults ? undefined : resetLineToDefault}
            getLineDefault={
              editingDefaults
                ? undefined
                : (catKey, lineId) => {
                    const d = defaultLines.get(`${catKey}:${lineId}`);
                    if (!d) return null;
                    return d.type === 'hourly'
                      ? (d.wage || 0) * (d.hours || 0)
                      : d.total || 0;
                  }
            }
          />

          <PrimaryButton onClick={handleSave} disabled={!dirty}>
            {dirty
              ? editingDefaults
                ? 'Save monthly defaults'
                : `Save ${monthLabel(costMonth)} costs`
              : 'All changes saved'}
          </PrimaryButton>

          {savedFlash ? (
            <Box
              sx={{
                mt: 1.5,
                backgroundColor: colors.successSurface,
                borderRadius: 2.5,
                p: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: typography.body,
                  fontWeight: 700,
                  color: colors.successSurfaceForeground,
                }}
              >
                {editingDefaults
                  ? 'Monthly defaults saved'
                  : `Saved for ${monthLabel(costMonth)}`}
              </Typography>
            </Box>
          ) : null}
        </>
      ) : null}

      {!editingDefaults ? <MonthDocs costMonth={costMonth} /> : null}
      {!editingDefaults ? <AdvancedCostTools /> : null}
    </Box>
  );
}

function ModeButton({ active, icon: Icon, label, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        py: 1,
        px: 1.5,
        border: 'none',
        borderRadius: 2.5,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: typography.caption,
        fontWeight: 600,
        backgroundColor: active ? colors.primary : colors.muted,
        color: active ? colors.primaryForeground : colors.mutedForeground,
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
      {label}
    </Box>
  );
}

/* ─────────────────────── Advanced cost tools ─────────────────────── */

/**
 * Setting monthly defaults from a month and copying costs between months are
 * built on the backend but not yet released, so the section announces itself
 * and stays inert — matching the mobile app.
 */
function AdvancedCostTools() {
  return (
    <Box sx={{ mt: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockOutlinedIcon sx={{ fontSize: 16, color: colors.faint }} />
        <Typography sx={{ ...smallCapsSx, mb: 0, color: colors.faint }}>
          Advanced
        </Typography>
        <Box
          sx={{
            backgroundColor: colors.muted,
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.5,
              color: colors.mutedForeground,
            }}
          >
            COMING SOON
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: typography.caption,
          color: colors.mutedForeground,
          mt: 0.75,
        }}
      >
        Tools like setting monthly defaults and copying costs between months are
        coming soon.
      </Typography>
    </Box>
  );
}

/* ─────────────────────────── Cost Documents ─────────────────────────── */

function MonthDocs({ costMonth }) {
  const { data: invoices } = useInvoices(costMonth);
  const createInvoice = useCreateInvoice();
  const deleteInvoice = useDeleteInvoice();
  const imageInputRef = useRef(null);

  const [category, setCategory] = useState('food');
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');
  const [image, setImage] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  function onImageChange(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    // The invoice image travels inside the JSON body, so it is read as a data
    // URL rather than posted as multipart.
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') setImage(result);
    };
    reader.readAsDataURL(file);
  }

  const amt = parseFloat(amount);
  // An image is optional — a document can be just an amount and a label.
  const canAdd = !isNaN(amt) && amt > 0 && !!category;

  async function handleAdd() {
    if (!canAdd) return;
    const lbl =
      label.trim() ||
      `${DOC_CATS.find((c) => c.key === category)?.label ?? category} invoice`;
    await createInvoice.mutateAsync({
      month: costMonth,
      category,
      amount: amt,
      label: lbl,
      image: image ?? undefined,
      date: new Date().toISOString().slice(0, 10),
    });
    setAmount('');
    setLabel('');
    setImage(null);
    setAddOpen(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  }

  const list = invoices ?? [];
  // Group by category, keeping DOC_CATS order; unknown categories last.
  const knownKeys = DOC_CATS.map((c) => c.key);
  const orderedKeys = [
    ...knownKeys.filter((k) => list.some((i) => i.category === k)),
    ...Array.from(
      new Set(list.map((i) => i.category).filter((k) => !knownKeys.includes(k)))
    ),
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography sx={{ ...smallCapsSx, mb: 0 }}>
          Documents for {monthLabel(costMonth)}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => setAddOpen(true)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            py: 0.75,
            px: 1.75,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: typography.caption,
            fontWeight: 700,
            color: colors.primary,
            backgroundColor: 'transparent',
            border: `1px solid ${colors.primary}`,
            borderRadius: 2.5,
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          Add Document
        </Box>
      </Box>
      <Typography sx={subSx}>
        Upload a photo of an invoice or receipt — utility bill, food supply,
        anything. It adds to that category&apos;s costs for{' '}
        {monthLabel(costMonth)}.
      </Typography>

      {savedFlash ? (
        <Typography
          sx={{
            fontSize: typography.body,
            fontWeight: 700,
            color: colors.successSurfaceForeground,
            mb: 1,
          }}
        >
          Saved
        </Typography>
      ) : null}

      {list.length === 0 ? (
        <Typography
          sx={{ fontSize: typography.caption, color: colors.faint, py: 1 }}
        >
          No documents for this month yet.
        </Typography>
      ) : (
        orderedKeys.map((key) => {
          const cat = DOC_CATS.find((c) => c.key === key);
          const Icon = cat?.icon ?? DescriptionOutlinedIcon;
          const group = list.filter((i) => i.category === key);
          const groupTotal = group.reduce((acc, i) => acc + i.amount, 0);
          return (
            <Box key={key} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 1,
                  mb: 0.5,
                }}
              >
                <Icon sx={{ fontSize: 15, color: colors.mutedForeground }} />
                <Typography sx={{ ...smallCapsSx, mb: 0 }}>
                  {cat?.label ?? key}
                </Typography>
                <Typography
                  sx={{ fontSize: typography.caption, color: colors.faint }}
                >
                  · {group.length} doc{group.length > 1 ? 's' : ''} ·{' '}
                  {fmt(groupTotal)}
                </Typography>
              </Box>
              {group.map((inv) => (
                <Box
                  key={inv._id}
                  sx={{
                    ...cardSx,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.25,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      flexShrink: 0,
                      backgroundColor: colors.muted,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon
                      sx={{ fontSize: 17, color: colors.mutedForeground }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: typography.small,
                        fontWeight: 600,
                        color: colors.foreground,
                      }}
                    >
                      {inv.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: typography.caption,
                        color: colors.mutedForeground,
                      }}
                    >
                      {cat?.label ?? inv.category} · {inv.date}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: typography.small,
                      fontWeight: 700,
                      color: colors.foreground,
                    }}
                  >
                    {fmt(inv.amount)}
                  </Typography>
                  {inv.image ? (
                    <IconButton size="small" onClick={() => setViewing(inv)}>
                      <VisibilityOutlinedIcon
                        sx={{ fontSize: 20, color: colors.primary }}
                      />
                    </IconButton>
                  ) : null}
                  <IconButton size="small" onClick={() => setDeleting(inv)}>
                    <DeleteOutlineIcon
                      sx={{ fontSize: 20, color: colors.destructive }}
                    />
                  </IconButton>
                </Box>
              ))}
            </Box>
          );
        })
      )}

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ p: 2.75 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.25,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.subtitle,
                fontWeight: 700,
                color: colors.foreground,
              }}
            >
              Add document — {monthLabel(costMonth)}
            </Typography>
            <IconButton size="small" onClick={() => setAddOpen(false)}>
              <CloseIcon sx={{ color: colors.mutedForeground }} />
            </IconButton>
          </Box>

          <Typography sx={smallCapsSx}>Cost category</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            {DOC_CATS.map((c) => {
              const active = category === c.key;
              const Icon = c.icon;
              return (
                <Box
                  component="button"
                  type="button"
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    py: 0.75,
                    px: 1.25,
                    border: 'none',
                    borderRadius: 999,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: typography.caption,
                    fontWeight: 600,
                    backgroundColor: active ? colors.primary : colors.muted,
                    color: active
                      ? colors.primaryForeground
                      : colors.mutedForeground,
                  }}
                >
                  <Icon sx={{ fontSize: 15 }} />
                  {c.label}
                </Box>
              );
            })}
          </Box>

          <Box
            component="input"
            type="file"
            accept="image/*"
            ref={imageInputRef}
            onChange={onImageChange}
            sx={{ display: 'none' }}
          />

          {image ? (
            <Box sx={{ position: 'relative', mb: 1.5 }}>
              <Box
                component="img"
                src={image}
                alt="Invoice preview"
                sx={{
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'contain',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.border}`,
                }}
              />
              <IconButton
                size="small"
                onClick={() => setImage(null)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 17, color: '#fff' }} />
              </IconButton>
            </Box>
          ) : (
            <Box
              component="button"
              type="button"
              onClick={() => imageInputRef.current?.click()}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                py: 3,
                px: 2,
                mb: 1.5,
                cursor: 'pointer',
                fontFamily: 'inherit',
                border: `2px dashed ${colors.primary}`,
                backgroundColor: colors.accent,
                borderRadius: 3,
              }}
            >
              <CameraAltOutlinedIcon
                sx={{ fontSize: 30, color: colors.primary }}
              />
              <Typography
                sx={{
                  fontSize: typography.body,
                  fontWeight: 700,
                  color: colors.primary,
                }}
              >
                Choose an image
              </Typography>
              <Typography
                sx={{ fontSize: typography.caption, color: colors.faint }}
              >
                Invoice or receipt image (optional)
              </Typography>
            </Box>
          )}

          <Typography sx={{ ...smallCapsSx, mb: 0.5 }}>Amount ($)</Typography>
          <Box
            component="input"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            sx={{ ...inputSx, mb: 1.5 }}
          />

          <Typography sx={{ ...smallCapsSx, mb: 0.5 }}>
            Label (optional)
          </Typography>
          <Box
            component="input"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={`e.g. ${
              DOC_CATS.find((c) => c.key === category)?.label ?? category
            } — Sysco`}
            sx={{ ...inputSx, mb: 2 }}
          />

          <PrimaryButton
            onClick={handleAdd}
            disabled={!canAdd || createInvoice.isPending}
          >
            {createInvoice.isPending ? 'Adding…' : 'Add document'}
          </PrimaryButton>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ p: 2.75 }}>
          <Typography
            sx={{
              fontSize: typography.emphasis,
              fontWeight: 700,
              color: colors.foreground,
            }}
          >
            Delete this document?
          </Typography>
          <Typography
            sx={{
              fontSize: typography.caption,
              color: colors.mutedForeground,
              mt: 0.75,
            }}
          >
            “{deleting?.label}” · {deleting ? fmt(deleting.amount) : ''} will be
            removed from {monthLabel(costMonth)}&apos;s costs. This can&apos;t
            be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.25, mt: 2 }}>
            <PrimaryButton
              onClick={() => setDeleting(null)}
              sx={{
                flex: 1,
                backgroundColor: colors.muted,
                color: colors.foreground,
              }}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              tone={colors.destructive}
              onClick={() => {
                deleteInvoice.mutate(deleting._id);
                setDeleting(null);
              }}
              sx={{ flex: 1 }}
            >
              Delete
            </PrimaryButton>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ p: 2.75 }}>
          {viewing?.image ? (
            <Box
              component="img"
              src={viewing.image}
              alt={viewing.label}
              sx={{
                width: '100%',
                maxHeight: 460,
                objectFit: 'contain',
                borderRadius: 2.5,
              }}
            />
          ) : null}
          <Typography
            sx={{
              fontSize: typography.caption,
              color: colors.mutedForeground,
              textAlign: 'center',
              my: 1.5,
            }}
          >
            {viewing?.label} · {viewing ? fmt(viewing.amount) : ''}
          </Typography>
          <PrimaryButton onClick={() => setViewing(null)}>Close</PrimaryButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── Month picker ─────────────────────────── */

function MonthPicker({ value, onSelect, markCurrent }) {
  const [anchor, setAnchor] = useState(null);
  const options = buildMonthOptions();
  const currentKey = currentMonthStr();

  return (
    <Box sx={{ mb: 1.75 }}>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          maxWidth: 320,
          px: 1.75,
          py: 1.25,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: typography.body,
          fontWeight: 600,
          color: colors.foreground,
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: 2.5,
        }}
      >
        {monthLabel(value)}
        <ExpandMoreIcon sx={{ fontSize: 20, color: colors.mutedForeground }} />
      </Box>

      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { maxHeight: 380, minWidth: 260 } }}
      >
        {options.map((m) => {
          const active = m.key === value;
          return (
            <MenuItem
              key={m.key}
              selected={active}
              onClick={() => {
                onSelect(m.key);
                setAnchor(null);
              }}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                fontSize: typography.body,
                fontWeight: active ? 700 : 500,
                color: active ? colors.primary : colors.foreground,
              }}
            >
              {m.label}
              {markCurrent && m.key === currentKey ? ' (current)' : ''}
              {active ? (
                <CheckIcon sx={{ fontSize: 19, color: colors.primary }} />
              ) : null}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
